import { useEffect, useState, type FormEvent } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import { MessageThread } from "../components/MessagesLayout";
import { useCommunityChannels } from "../hooks/useCommunityChannels";
import { useCommunity } from "../hooks/useCommunity";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { revokeConversation } from "../../../common/hooks/conversationCacheAuthority";

export function ChannelThreadPage() {
  const { communityId = "", conversationId = "" } = useParams();
  const { user, loading } = useAuth();
  if (loading || !user) return <Typography role="status">
    {loading ? "Loading channel…" : "Sign in to view this channel."}
  </Typography>;
  return (
    <ScopedChannelThread
      key={`${user.uid}:${communityId}:${conversationId}`}
      communityId={communityId}
      conversationId={conversationId}
    />
  );
}

function ScopedChannelThread({
  communityId,
  conversationId,
}: {
  communityId: string;
  conversationId: string;
}) {
  const { user } = useAuth();
  const isNarrow = useIsNarrow();
  const client = useQueryClient();
  const { channels, update, archive } = useCommunityChannels(communityId);
  const communityQ = useCommunity(communityId);
  const channel = channels.data?.items.find(
    (item) =>
      item.conversation_id === conversationId &&
      item.community_id === communityId,
  );
  const backTo = `/community/communities/${communityId}?view=chat`;
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const unavailable =
    channels.isError || (channels.data !== undefined && !channel);

  useEffect(() => {
    if (unavailable && conversationId)
      void revokeConversation(client, conversationId);
  }, [client, conversationId, unavailable]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !channel?.can_manage ||
      channel.archived_at ||
      !name.trim() ||
      update.isPending
    )
      return;
    try {
      await update.mutateAsync({
        conversationId,
        data: { name: name.trim(), description: description.trim() || null },
      });
      setEditing(false);
    } catch {
      // Preserve the draft; owner authority refreshes and errors remain visible.
    }
  }

  if (unavailable)
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Alert severity="warning">
          This channel is unavailable. Your access may have changed.
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to={backTo} size="small">
            Back to community
          </Button>
          <Button size="small" onClick={() => void channels.refetch()}>
            Retry
          </Button>
        </Stack>
      </Stack>
    );
  if (!channel)
    return (
      <Typography role="status" sx={{ p: 2 }}>
        Loading channel…
      </Typography>
    );

  const canManage = channel.can_manage && !channel.archived_at;
  const headerActions = canManage ? (
    <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
      <Button
        size="small"
        onClick={() => {
          setName(channel.name);
          setDescription(channel.description ?? "");
          update.reset();
          setEditing(true);
        }}
      >
        Edit channel
      </Button>
      <Button
        size="small"
        onClick={() => {
          archive.reset();
          setConfirmArchive(true);
        }}
      >
        Archive channel
      </Button>
    </Stack>
  ) : null;

  return (
    <Stack
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        height: {
          xs: "calc(100dvh - 8.75rem - env(safe-area-inset-bottom, 0px))",
          sm: "100%",
        },
      }}
    >
      <MessageThread
        conversationId={conversationId}
        communityId={communityId}
        title={channel.name}
        headingLevel="h1"
        backTo={backTo}
        backLabel="Back to community"
        writeAccess={channel.write_access}
        canModerateMessages={channel.can_moderate_messages === true}
        canDeleteOwnMessages={channel.can_delete_own_messages === true}
        authenticated={Boolean(user)}
        isNarrow={isNarrow}
        headerActions={headerActions}
        subtitle={
          communityQ.data
            ? `${communityQ.data.name} · ${communityQ.data.member_count} ${communityQ.data.member_count === 1 ? "member" : "members"}`
            : undefined
        }
        composerPlaceholder={`Message #${channel.name}`}
      />
      {canManage ? (
        <>
          <Dialog
            open={editing}
            onClose={() => {
              if (!update.isPending) setEditing(false);
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Channel details</DialogTitle>
            <Stack component="form" onSubmit={(event) => void save(event)}>
              <DialogContent>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    size="small"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                    slotProps={{ htmlInput: { maxLength: 80 } }}
                  />
                  <TextField
                    label="Description"
                    size="small"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    multiline
                    minRows={2}
                    slotProps={{ htmlInput: { maxLength: 2000 } }}
                  />
                  {update.isError ? (
                    <Alert severity="error">
                      Could not save the channel. Please try again.
                    </Alert>
                  ) : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  disabled={update.isPending}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="submit"
                  disabled={update.isPending || !name.trim()}
                >
                  Save changes
                </Button>
              </DialogActions>
            </Stack>
          </Dialog>
          <Dialog
            open={confirmArchive}
            onClose={() => {
              if (!archive.isPending) setConfirmArchive(false);
            }}
          >
            <DialogTitle>Archive channel?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Members can still read its history, but no new messages can be
                sent.
              </DialogContentText>
              {archive.isError ? (
                <Alert severity="error">
                  Could not archive the channel. Please try again.
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button
                size="small"
                disabled={archive.isPending}
                onClick={() => setConfirmArchive(false)}
              >
                Cancel
              </Button>
              <Button
                size="small"
                disabled={archive.isPending}
                onClick={() => {
                  void archive
                    .mutateAsync(conversationId)
                    .then(() => setConfirmArchive(false))
                    .catch(() => undefined);
                }}
              >
                Confirm archive
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </Stack>
  );
}
